// exportData
//
// Helpers for downloading whatever rows are currently displayed in the
// Raw Data page (or any other page that loads cached records) as a CSV
// or .xlsx file. Both exports use the same flattening so the column
// layout matches what the user sees in the Raw view of DataTable:
// every top-level key plus every nested data.* / metadata.* field.

import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { extractBirthYear } from './dateUtils';
import {
  ALWAYS_INCLUDE_REFERRAL_DATA_FIELDS,
  isReferralRawDataType,
} from './referralRawColumns';

const DOB_FIELD_RX = /(^|\.)(patient_dob|patient_date|date_of_birth|birth_date|dob)$/i;

function isDobField(key) {
  return DOB_FIELD_RX.test(key || '');
}

/**
 * Flatten one record into a plain object whose keys are dotted paths.
 *
 * Top-level scalars stay as their key (e.g. `id`, `dataDate`). Nested
 * `data` and `metadata` objects expose each of their own keys as
 * `data.<key>` / `metadata.<key>`. Anything that's still an object after
 * one level of flattening (rare — usually arrays of patient hashes) is
 * JSON-stringified so it lands in a single cell.
 */
function flattenRecord(record, { deidentifyDob = false } = {}) {
  const out = {};
  for (const [k, v] of Object.entries(record || {})) {
    if (k === 'data' || k === 'metadata') continue;
    out[k] = formatScalar(v, k, deidentifyDob);
  }
  if (record?.data && typeof record.data === 'object') {
    for (const [k, v] of Object.entries(record.data)) {
      out[`data.${k}`] = formatScalar(v, `data.${k}`, deidentifyDob);
    }
  }
  if (record?.metadata && typeof record.metadata === 'object') {
    for (const [k, v] of Object.entries(record.metadata)) {
      out[`metadata.${k}`] = formatScalar(v, `metadata.${k}`, deidentifyDob);
    }
  }
  return out;
}

function formatScalar(v, key = '', deidentifyDob = false) {
  if (v === null || v === undefined) return '';
  if (deidentifyDob && isDobField(key)) {
    return extractBirthYear(v);
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return v;
}

/**
 * Build the column list as the union of every flattened key across all
 * rows. Sorted so the file is stable run-to-run; top-level fields come
 * first (no dot), then data.*, then metadata.*.
 */
function collectColumns(flatRows, { dataType } = {}) {
  const seen = new Set();
  flatRows.forEach(r => Object.keys(r).forEach(k => seen.add(k)));
  if (isReferralRawDataType(dataType)) {
    ALWAYS_INCLUDE_REFERRAL_DATA_FIELDS.forEach(f => seen.add(`data.${f}`));
  }
  return [...seen].sort((a, b) => {
    const aPriority = a.startsWith('metadata.') ? 2 : a.startsWith('data.') ? 1 : 0;
    const bPriority = b.startsWith('metadata.') ? 2 : b.startsWith('data.') ? 1 : 0;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return a.localeCompare(b);
  });
}

/** Trigger a browser download for the given Blob. */
export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke a tick so the click handler completes in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

/** Build a stable filename stem like `referrals_2026-01-01_to_2026-04-30`. */
export function buildFilenameStem(dataType, startDate, endDate, suffix = '') {
  const stem = (dataType || 'data').replace(/[^a-z0-9]+/gi, '_');
  const base = (() => {
    if (startDate && endDate) return `${stem}_${startDate}_to_${endDate}`;
    if (startDate) return `${stem}_${startDate}`;
    return `${stem}_${new Date().toISOString().slice(0, 10)}`;
  })();
  return suffix ? `${base}_${suffix}` : base;
}

/**
 * Download the supplied records as a CSV file. `records` is the same
 * shape we get back from the cache endpoints (each item has a `data`
 * blob and assorted top-level fields).
 */
export function exportRecordsToCsv(records, { dataType, startDate, endDate, deidentifyDob = false } = {}) {
  const flat = (records || []).map(r => flattenRecord(r, { deidentifyDob }));
  const columns = collectColumns(flat, { dataType });
  const objects = flat.map(r => {
    const o = {};
    columns.forEach(h => { o[h] = r[h] ?? ''; });
    return o;
  });
  const csv = Papa.unparse({ fields: columns, data: objects });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${buildFilenameStem(dataType, startDate, endDate)}.csv`);
}

/**
 * Download the supplied records as an .xlsx workbook with a single
 * "Data" sheet. SheetJS handles type coercion — numeric strings stay
 * as strings here because the upstream cache schema is mostly text;
 * Excel will let the user re-type columns from the UI if needed.
 */
export function exportRecordsToXlsx(records, { dataType, startDate, endDate, deidentifyDob = false } = {}) {
  const flat = (records || []).map(r => flattenRecord(r, { deidentifyDob }));
  const columns = collectColumns(flat, { dataType });
  const objects = flat.map(r => {
    const o = {};
    columns.forEach(h => { o[h] = r[h] ?? ''; });
    return o;
  });

  // json_to_sheet picks columns from the first row by default; passing
  // `header` ensures every column appears even if the first record is
  // sparse.
  const sheet = XLSX.utils.json_to_sheet(objects, { header: columns });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Data');

  const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([arrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${buildFilenameStem(dataType, startDate, endDate)}.xlsx`);
}
